import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Alert, Badge, Button, Card } from '../../components/ui';
import { api } from '../../lib/api';
import { KbUpdatesPanel, type ActiveInfo } from './KbUpdatesPanel';
import { MfdsKbPanel } from './MfdsKbPanel';

interface KbStatus {
  seeded: boolean;
  seedVersion: { label: string; ecfrAsOf: string; generated: string; geminiModel: string };
  active: ActiveInfo;
  fileCount: number;
  bundledFileCount: number;
  byCheckType: Record<
    string,
    { bundled: number; selected: number; selectedBytes: number; paths: string[]; unresolved: string[] }
  >;
  files: {
    kbFileId: string;
    path: string;
    title: string;
    citation: string;
    kind: string;
    checkTypes: string[];
    bytes: number;
    asOf: string;
  }[];
}
export function KbPage() {
  const { t } = useTranslation();
  const [st, setSt] = useState<KbStatus | null>(null);
  const [country, setCountry] = useState<'us-fda' | 'kr-mfds'>('us-fda');
  const [jobs, setJobs] = useState<
    {
      id: string;
      type: string;
      status: string;
      createdAt: string;
      lastError: string | null;
      ref: Record<string, string>;
    }[]
  >([]);
  const [msg, setMsg] = useState<string | null>(null);
  const load = async () => {
    setSt(await api<KbStatus>('/api/v1/superadmin/kb'));
    setJobs((await api<{ jobs: typeof jobs }>('/api/v1/superadmin/checks?limit=30')).jobs);
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  }, []);
  const seed = async () => {
    const r = await api<Record<string, unknown>>('/api/v1/superadmin/kb/seed', { method: 'POST' });
    setMsg(JSON.stringify(r));
    await load();
  };
  if (!st) return <p className="text-slate-500">{t('app.loading')}</p>;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/superadmin" className="text-sm text-emerald-700 underline">
          ← {t('sa.back')}
        </Link>
        <h1 className="text-2xl font-semibold">{t('sakb.title')}</h1>
        <Badge tone={st.seeded ? 'green' : 'amber'}>
          {st.seeded ? t('sakb.seeded') : t('sakb.notSeeded')}
        </Badge>
        <Button variant={country === 'us-fda' ? 'primary' : 'secondary'} onClick={() => setCountry('us-fda')}>
          FDA(US)
        </Button>
        <Button
          variant={country === 'kr-mfds' ? 'primary' : 'secondary'}
          onClick={() => setCountry('kr-mfds')}
        >
          MFDS(KR)
        </Button>
        {country === 'us-fda' && (
          <Button className="ml-auto" onClick={() => void seed()}>
            {t('sakb.seed')}
          </Button>
        )}
      </div>
      {country === 'kr-mfds' && <MfdsKbPanel />}
      <div className={country === 'us-fda' ? 'space-y-4' : 'hidden'}>
        {msg && <Alert tone="green">{msg}</Alert>}
        <KbUpdatesPanel active={st.active} byCheckType={st.byCheckType} onPublished={() => void load()} />
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <dl className="grid grid-cols-[10rem_1fr] gap-y-1 text-sm">
              <dt className="text-slate-500">{t('sakb.version')}</dt>
              <dd className="font-mono">{st.seedVersion.label}</dd>
              <dt className="text-slate-500">{t('sakb.ecfr')}</dt>
              <dd>{st.seedVersion.ecfrAsOf}</dd>
              <dt className="text-slate-500">{t('sakb.gemini')}</dt>
              <dd>{st.seedVersion.geminiModel}</dd>
              <dt className="text-slate-500">{t('sakb.files')}</dt>
              <dd>
                {st.fileCount} / {st.bundledFileCount}
              </dd>
            </dl>
          </Card>
          <Card title={t('sakb.coverage')}>
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th>{t('sakb.checkType')}</th>
                  <th>{t('sakb.bundled')}</th>
                  <th>{t('sakb.selected')}</th>
                  <th>{t('sakb.bytes')}</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(st.byCheckType).map(([k, v]) => (
                  <tr key={k}>
                    <td className="py-1 font-mono text-xs">{k}</td>
                    <td>{v.bundled}</td>
                    <td>{v.selected}</td>
                    <td>
                      {Math.round(v.selectedBytes / 1024)} KB (~{Math.round(v.selectedBytes / 4 / 1000)}k
                      tokens)
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
        <Card title={t('sakb.jobs')}>
          <ul className="divide-y divide-slate-100 text-xs">
            {jobs.map((j) => (
              <li key={j.id} className="flex gap-3 py-1">
                <span className="w-36 font-mono text-slate-500">
                  {j.createdAt.slice(0, 16).replace('T', ' ')}
                </span>
                <Badge tone={j.status === 'done' ? 'green' : j.status === 'failed' ? 'red' : 'blue'}>
                  {j.status}
                </Badge>
                <span className="font-mono">{j.ref.checkId}</span>
                <span className="truncate text-red-600">{j.lastError}</span>
              </li>
            ))}
          </ul>
        </Card>
        <Card title={`${t('sakb.files')} (${st.files.length})`}>
          <table className="w-full text-xs">
            <thead className="text-left uppercase text-slate-500">
              <tr>
                <th className="py-1">{t('sakb.file')}</th>
                <th>{t('sakb.citation')}</th>
                <th>{t('sakb.kind')}</th>
                <th>{t('sakb.checkType')}</th>
                <th>KB</th>
                <th>{t('sakb.asOf')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {st.files.map((f) => (
                <tr key={f.kbFileId}>
                  <td className="py-1 font-mono">{f.path}</td>
                  <td>{f.citation}</td>
                  <td>
                    <Badge tone={f.kind === 'ecfr' ? 'green' : 'amber'}>{f.kind}</Badge>
                  </td>
                  <td>{f.checkTypes.join(', ')}</td>
                  <td>{Math.round(f.bytes / 1024)}</td>
                  <td>{f.asOf}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
